import {useMemo} from "react";
import {PauseIcon, PlayIcon, SpeakerWaveIcon} from "@heroicons/react/20/solid";
import {useIonoNowPlaying} from "@/hooks/useIonoNowPlaying";
import {useGlobalAudio} from "@/context/AudioProvider.tsx";

type Props = {
    src?: string;
    programTitle?: string;
    programProducer?: string;
    programDescription?: string;
    className?: string;
    streamId?: number | string;
};

export default function LiveStreamPlayer({
                                             src = "https://edge.iono.fm/xice/88_high.aac",
                                             programTitle = "Kosmos 94.1",
                                             programProducer = "",
                                             programDescription = "",
                                             className = "",
                                             streamId = 88,
                                         }: Props) {

    const {audio, setSrc, play, pause, playing, loading, error, volume, setVolume} = useGlobalAudio();
    const now = useIonoNowPlaying(streamId);

    useMemo(() => {
        if (audio) setSrc(src);
    }, [audio, src, setSrc]);

    const currentTitle = now?.title || programTitle;
    const currentArtist = now?.artist || programProducer

    return (
        <div
            className={"rounded-xl bg-white/90 backdrop-blur ring-1 ring-gray-200 shadow-sm px-4 py-3 flex items-center gap-4 max-w-lg " + className}>
            <button
                type="button"
                onClick={() => (playing ? pause() : play().catch(() => {
                }))}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-900 text-white hover:bg-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                aria-label={playing ? "Pouse" : "Speel"}
            >
                {playing ? <PauseIcon className="size-6"/> : <PlayIcon className="size-6"/>}
            </button>

            <div className="min-w-0 flex-1 text-sm">
                <div className="font-semibold text-gray-800">{currentTitle}</div>
                <div className="text-blue-900 font-semibold">{currentArtist}</div>
                <div className="text-gray-700">{programDescription}</div>
            </div>

            <div className="hidden sm:flex items-center gap-2 min-w-[140px]">
                <SpeakerWaveIcon className="size-5 text-gray-500"/>
                <input
                    type="range" min={0} max={1} step={0.01}
                    value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    aria-label="Volume"
                    className="w-28 accent-blue-900"
                />
            </div>
        </div>
    );
}
